<template>
    <div class="row w-100">
        <div class="position-relative w-100">
            <h4 class="text-dark text-capitalize my-4">Customize your data trackers</h4>
            <p class="mb-5" v-html="descriptions.intro"></p>

            <div class="row pt-2">
                <div class="col-12 col-md-6 offset-xl-1 col-xl-5">
                    <div v-for="t in trackers.filter(t => t.type === 'percent')">
                        <customize
                            :type="t.type"
                            @toggle="toggleTracker(t, $event)"
                            :active="t.is_active"
                            :description="descriptions[t.type]"
                        >
                            <template slot="customizations">
                                <customize-iconset
                                    :saving="saving"
                                    :tracker="t"
                                    @save-tracker-customization="saveTrackerCustomization(t)"
                                    @select-iconset="selectIconset(t, $event)"
                                    @edit-tracker="editTracker(t)"
                                ></customize-iconset>
                            </template>
                        </customize>
                    </div>
                </div>

                <div class="col-12 col-md-6 col-xl-5">
                    <div v-for="t in trackers.filter(t => t.type === 'multi_percent')">
                        <customize
                            :type="t.type"
                            @toggle="toggleTracker(t, $event)"
                            :active="t.is_active"
                            :description="descriptions[t.type]"
                        >
                            <template slot="customizations">
                                <customize-iconset
                                    :saving="saving"
                                    :tracker="t"
                                    @save-tracker-customization="saveTrackerCustomization(t)"
                                    @select-iconset="selectIconset(t, $event)"
                                    @edit-tracker="editTracker(t)"
                                ></customize-iconset>
                            </template>
                        </customize>
                    </div>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'tally')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-2">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_tally.png"
                                    alt=""
                                />
                            </div>
                        </template>
                    </customize>
                </div>
                <div
                    class="col-12 col-md-6 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'multi_tally')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-2">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_multi_tally.png"
                                    alt=""
                                />
                            </div>
                        </template>
                    </customize>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'cold_probe')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-5">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_cold_probe.png"
                                    alt=""
                                />
                            </div>
                        </template>
                    </customize>
                </div>
                <div
                    class="col-12 col-md-6 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'preference_assessment')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div
                                class="px-2"
                                v-if="
                                    t.hasOwnProperty('default_options') &&
                                        t.default_options &&
                                        t.default_options.hasOwnProperty('types')
                                "
                            >
                                <small class="font-weight-bold text-primary"
                                    ><i class="fa fa-edit mr-2"></i>Select Preference Assessment
                                    Options</small
                                >
                                <div v-for="(p, i) in t.default_options.types" :key="i">
                                    <toggle-button
                                        :value="p.active"
                                        @change="setPreferenceAssessment(p, $event)"
                                        :sync="true"
                                        :labels="true"
                                    />
                                    <span class="ml-2 text-capitalize'">{{
                                        preferenceDescription(p.name)
                                    }}</span>
                                </div>
                                <transition name="simple-fade">
                                    <div v-if="preferenceAssessmentChanged">
                                        <small
                                            v-if="preferenceAssessmentChanged"
                                            class="text-warning font-italic"
                                            ><i class="fa fa-exclamation-triangle mr-2"></i>Remember
                                            to save your customization below</small
                                        >
                                    </div>
                                </transition>
                                <span
                                    class="cursor-pointer font-weight-bold text-primary"
                                    @click="saveTrackerCustomization(t)"
                                    v-html="
                                        saving === t.type
                                            ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i>Saving...`
                                            : `<i class='fa fa-save mr-2'></i>Save`
                                    "
                                ></span>
                            </div>
                        </template>
                    </customize>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'task_analysis')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <customize-iconset
                                :saving="saving"
                                :tracker="t"
                                @save-tracker-customization="saveTrackerCustomization(t)"
                                @select-iconset="selectIconset(t, $event)"
                                @edit-tracker="editTracker(t)"
                            ></customize-iconset>
                        </template>
                    </customize>
                </div>
                <div class="col-12 col-md-6 col-xl-5">
                    <div v-for="t in trackers.filter(t => t.type === 'articulation')">
                        <customize
                            :type="t.type"
                            @toggle="toggleTracker(t, $event)"
                            :active="t.is_active"
                            :description="descriptions[t.type]"
                        >
                            <template slot="customizations">
                                <customize-iconset
                                    :saving="saving"
                                    :tracker="t"
                                    @save-tracker-customization="saveTrackerCustomization(t)"
                                    @select-iconset="selectIconset(t, $event)"
                                    @edit-tracker="editTracker(t)"
                                ></customize-iconset>
                            </template>
                        </customize>
                    </div>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'cme')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-5">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_cme.png"
                                    alt=""
                                />
                            </div>
                        </template>
                    </customize>
                </div>
                <div
                    class="col-12 col-md-6 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'soap_note')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-2" v-if="t.hasOwnProperty('default_options')">
                                <div>
                                    <label for="soap-note" class="font-weight-bold">Plan</label>
                                </div>
                                <textarea
                                    v-if="
                                        t.default_options &&
                                            t.default_options.hasOwnProperty('plan')
                                    "
                                    id="soap-note"
                                    class="textarea w-100"
                                    @keyup="soapNotesChanged = true"
                                    v-model="t.default_options.plan"
                                ></textarea>
                                <transition name="simple-fade">
                                    <div v-if="soapNotesChanged">
                                        <small class="text-warning font-italic"
                                            ><i class="fa fa-exclamation-triangle mr-2"></i>Remember
                                            to save your customization below</small
                                        >
                                    </div>
                                </transition>
                                <span
                                    class="cursor-pointer font-weight-bold text-primary"
                                    @click="saveTrackerCustomization(t)"
                                    v-html="
                                        saving === t.type
                                            ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i>Saving...`
                                            : `<i class='fa fa-save mr-2'></i>Save`
                                    "
                                ></span>
                            </div>
                        </template>
                    </customize>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'mand')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-5">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_mand.png"
                                    alt=""
                                />
                            </div>
                            <!--
                            <customize-iconset :saving="saving" :tracker="t" @save-tracker-customization="saveTrackerCustomization(t)" @select-iconset="selectIconset(t,$event)"  @edit-tracker="editTracker(t)" ></customize-iconset>
-->
                        </template>
                    </customize>
                </div>

                <div
                    class="col-12 col-md-6 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'mand_echoic')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-5">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_echoic.png"
                                    alt=""
                                />
                            </div>
                            <!--
                            <customize-iconset :saving="saving" :tracker="t" @save-tracker-customization="saveTrackerCustomization(t)" @select-iconset="selectIconset(t,$event)"  @edit-tracker="editTracker(t)" ></customize-iconset>
-->
                        </template>
                    </customize>
                </div>
            </div>

            <div class="row pt-2">
                <div
                    class="col-12 col-md-6 offset-xl-1 col-xl-5"
                    v-for="t in trackers.filter(t => t.type === 'time_sampling')"
                >
                    <customize
                        :type="t.type"
                        @toggle="toggleTracker(t, $event)"
                        :active="t.is_active"
                        :description="descriptions[t.type]"
                    >
                        <template slot="customizations">
                            <div class="px-5">
                                <img
                                    class="img-fluid"
                                    src="../../../images/trackers/demo_time_sampling.png"
                                    alt=""
                                />
                            </div>
                        </template>
                    </customize>
                </div>
                <div class="col-12 col-md-6 col-xl-5">
                    <div v-for="t in trackers.filter(t => t.type === 'duration')">
                        <customize
                            :type="t.type"
                            @toggle="toggleTracker(t, $event)"
                            :active="t.is_active"
                            :description="descriptions[t.type]"
                        >
                            <template slot="customizations">
                                <div class="px-2">
                                    <img
                                        class="img-fluid col-12 col-md-6 col-xl-8"
                                        src="../../../images/trackers/demo_duration.png"
                                        alt=""
                                    />
                                </div>
                                <customize-iconset
                                    :saving="saving"
                                    :tracker="t"
                                    @save-tracker-customization="saveTrackerCustomization(t)"
                                    @select-iconset="selectIconset(t, $event)"
                                    @edit-tracker="editTracker(t)"
                                ></customize-iconset>
                            </template>
                        </customize>
                    </div>
                </div>
            </div>

            <div class="row mb-5"></div>
        </div>
    </div>
</template>

<script>
import Prompt from "../ui/PromptIcon";
import Customize from "./TrackerCustomize";
import CustomizeIconset from "./CustomizeIconset";

export default {
    name: "TrackerList",
    props: ["customizationOwner", "endpoint", "trackerTypes"],
    components: {
        Prompt,
        Customize,
        CustomizeIconset,
    },
    data() {
        return {
            saving: null,
            customizations: [],
            trackers: [],
            descriptions: {
                intro:
                    "We want you to get the most of tracking your data. That’s why we allow you to choose the data trackers you want and customize them to fit your business needs. Turn off trackers you don’t use so they don't get in the way of the ones you use frequently. Customize any tracker to fit your needs and preferences. If you don't see a feature you want in a tracker, let us know.",
                percent:
                    "This tracker allows you to record if a client was able to do something in trials and track that success rate as a percentage.",
                multi_percent:
                    "This tracker allows you to record if a client was able to do something in trials and track that success rate as a percentage.",
                tally:
                    "This tracker allows you to record how many times a client performs a task with or without a time limitation.",
                multi_tally:
                    "This tracker allows you to record how many times a client performs multiple tasks with or without a time limitation.",
                duration:
                    "This is a stop watch function to records the length of time it takes a client to perform a task.",
                cold_probe:
                    "This tracker is for ABA therapists to set a list of targets and record probes for baseline, teaching and mastery.",
                preference_assessment:
                    "This tracker is for ABA therapists to run various preference assessments to learn what rewards most motivate a client.",
                task_analysis:
                    "This tracker allows you to create a step-by-step process to teach a client a task (i.e. teeth brushing). With each step the professional can enter the prompt level.",
                articulation:
                    "This tracker is for speech therapists to select a target word and track the prompt needed.",
                soap_note:
                    "This tracker automatically generates your SOAP notes for you at the end of every session. You enter in the subjective (how the client is feeling), the tracker generates the objective (the goals you selected to track that day), and then it creates an assessment (the goals results that you tracked that day) that you can edit from the template created. ",
                mand:
                    "This is a tracker that allows you to track a request from the client for a desired behavior.",
                mand_echoic:
                    "Is a tracker for Speech Therapists that allows the therapist to log an imitated sound our word.",
                cme:
                    "This tracker is for occupational and physical therapists to estimate durations for an activity and then record the number.",
                time_sampling:
                    "This tracker is typically for ABA therapists and allows you to record data in time samples.",
            },
            preferenceAssessmentChanged: false,
            soapNotesChanged: false,
        };
    },
    created() {
        this.resetTrackers();
    },

    watch: {
        customizationOwner: {
            handler: function() {
                if (!this.customizations.length) {
                    this.getTrackerCustomizations();
                }
            },
            immediate: true,
        },
    },

    methods: {
        resetTrackers() {
            this.trackers = [];
            //Sets all possible default_options to an initial value to enable reactivity
            Object.keys(this.trackerTypes).forEach(k => {
                this.trackers.push({
                    type: k,
                    is_active: 1,
                    default_options: { types: [], plan: "", iconset: 0 },
                    editing: false,
                    customized: false,
                });
            });
            this.populateSOAPIfEmpty();
            this.populatePreferenceAssessmentIfEmpty();
            this.populateIconsetIfEmpty();
        },

        populateSOAPIfEmpty() {
            let soapTracker = this.trackers.find(t => t.type === "soap_note");
            if (!soapTracker.default_options.plan) {
                this.$set(soapTracker, "default_options", { plan: "Continue plan of care." });
            }
        },

        populatePreferenceAssessmentIfEmpty() {
            let preferenceAssessmentTracker = this.trackers.find(
                t => t.type === "preference_assessment"
            );
            if (!preferenceAssessmentTracker.default_options.types.length) {
                this.$set(preferenceAssessmentTracker, "default_options", {
                    types: [
                        { name: "paired", active: true },
                        { name: "single", active: true },
                        { name: "msw", active: true },
                        { name: "mswo", active: true },
                        { name: "free_operant", active: true },
                    ],
                });
            }
        },

        populateIconsetIfEmpty() {
            let iconsetTracker = this.trackers.filter(t =>
                [
                    "articulation",
                    "mand",
                    "mand_echoic",
                    "percent",
                    "task_analysis",
                    "duration",
                ].includes(t.type)
            );
            iconsetTracker.forEach(t => {
                if (!iconsetTracker.default_options || !iconsetTracker.default_options.iconset) {
                    this.$set(iconsetTracker, "default_options", { iconset: 0 });
                }
            });
        },

        getTrackerCustomizations() {
            if (_.get(this.customizationOwner, "id", false)) {
                axios
                    .get(
                        `/api/${this.endpoint}/${this.customizationOwner.id}/tracker_customizations`
                    )
                    .then(response => {
                        this.customizations = response.data.data;
                        this.parseCustomizations();
                    })
                    .catch(error => {
                        console.error(error);
                    });
            }
        },

        parseCustomizations() {
            this.resetTrackers();
            this.customizations.forEach(c => {
                if (c.default_options) {
                    // Filters out trackers that haven't been customized.
                    let t = this.trackers.find(t => t.type === c.type);
                    t.is_active = c.is_active;
                    t.default_options = c.default_options;
                    t.customized = true;
                }
            });
        },

        cleanTracker(tracker) {
            if (!tracker.hasOwnProperty("default_options") || !tracker.default_options) {
                tracker.default_options = [];
            }
            const keys = Object.keys(tracker.default_options);
            if (keys.length) {
                keys.forEach(key => {
                    if (
                        (!tracker.default_options[key] && isNaN(tracker.default_options[key])) ||
                        (Array.isArray(tracker.default_options[key]) &&
                            !tracker.default_options[key].length)
                    )
                        delete tracker.default_options[key];
                });
            }
            if (!Object.keys(tracker.default_options).length) {
                //CME, Cold Probe, Tally
                tracker.default_options = [];
            }
        },

        toggleTracker(tracker, event) {
            tracker.is_active = event.value;
            this.cleanTracker(tracker);
            axios
                .put(
                    `/api/${this.endpoint}/${this.customizationOwner.id}/tracker_customizations/${
                        tracker.type
                    }`,
                    tracker
                )
                .then(response => {
                    if (
                        response.data.tracker.default_options &&
                        response.data.tracker.default_options.hasOwnProperty("iconset")
                    ) {
                        tracker.default_options.iconset =
                            response.data.tracker.default_options.iconset;
                    }
                    tracker.is_active = response.data.tracker.is_active;
                    tracker.editing = false;
                    this.saving = null;
                })
                .catch(error => {
                    console.error(error);
                    this.saving = null;
                });
        },

        saveTrackerCustomization(tracker) {
            switch (tracker.type) {
                case "preference_assessment":
                    this.preferenceAssessmentChanged = false;
                    break;
                case "soap_note":
                    this.soapNotesChanged = false;
                    break;
            }
            this.saving = tracker.type;
            this.cleanTracker(tracker);
            axios
                .put(
                    `/api/${this.endpoint}/${this.customizationOwner.id}/tracker_customizations/${
                        tracker.type
                    }`,
                    tracker
                )
                .then(r => {
                    tracker.default_options.iconset = r.data.tracker.default_options.iconset;
                    tracker.editing = false;
                    this.saving = null;
                })
                .catch(error => {
                    console.error(error);
                    this.saving = null;
                });
        },

        editTracker(tracker) {
            tracker.editing = true;
        },

        selectIconset(tracker, iconset) {
            tracker.default_options.iconset = iconset;
        },

        setPreferenceAssessment(preference, event) {
            this.preferenceAssessmentChanged = true;
            preference.active = event.value;
        },

        preferenceDescription(name) {
            switch (name) {
                case "msw":
                    return "Multiple Stimulus with Replacement";
                case "mswo":
                    return "Multiple Stimulus without Replacement";
                default:
                    return name.replace("_", " ");
            }
        },
    },
};
</script>

<style scoped></style>
